import React, {
    forwardRef,
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
    ReactNode,
    MouseEvent,
} from 'react'
import { Box, Typography, Tabs, Tab } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfo } from '@fortawesome/free-solid-svg-icons'
import {
    BaseBlockProps,
    Dossier,
    DisplayItem as DisplayITemType,
    ContentItem,
    Pager,
    DossierData,
    BlockRef,
} from '../types'
import { useBlockData, useBlockSocket } from '../../../hooks'
import {
    translateDisplayItem,
    handleContentClick,
    shouldShowAddButton,
} from '../../../utils'
import {
    BlockWrapper,
    BlockHeader,
    BlockToolbar,
    LoadingState,
    DataTable,
} from '../base'
import {
    CardDefault,
    CardPerson,
    CardCarousel,
    CardTimeline,
} from '../../../Components/Display'
import { EventCalendarBlock } from './EventCalendarBlock'
import DossierModalNewButton from '@/Components/Elements/DossierModalNewButton'
import DossierModalEditButton from '@/Components/Elements/DossierModalEditButton'
import Pagination from '@/Components/Elements/Pagination'
import ContentDossierIndex from '@/feature/ContentDossier/Index/ContentDossierIndex'
import DisplayItem from '@/Components/DisplayItems/DisplayItem'
interface BlockDisplayProps extends BaseBlockProps {
    settingsLink?: { hide_add?: boolean }
}

export const BlockDisplay = forwardRef<BlockRef, BlockDisplayProps>(
    (
        {
            kedo,
            block,
            dashboard,
            onClose,
            onMax,
            onSuccess,
            getBlockHeader,
            dossier,
            link,
            misc,
            settingsLink,
        },
        ref
    ) => {
        const blockRef = useRef<BlockRef | null>(null)
        const [add, setAdd] = useState(false)
        const [activeTab, setActiveTab] = useState(0)

        const tabQueries = block.settings?.tab_queries
        const initialQuery = tabQueries?.[0]?.query ?? null

        const [query, setQuery] = useState<number | null>(initialQuery)

        const { data, loading, pager, page, setPage, refetch } =
            useBlockData<DossierData>({
                kedo,
                block,
                link,
                dossier,
                query,
            })

        const { emitUpdate } = useBlockSocket({
            kedo,
            block,
            blockRef,
        })

        const handleSuccess = useCallback(
            (dossierId: number) => {
                emitUpdate(dossierId)
                setAdd(false)
            },
            [emitUpdate]
        )

        const handleTabChange = useCallback(
            (_event: React.SyntheticEvent, newValue: number) => {
                setActiveTab(newValue)
                if (tabQueries?.[newValue]) {
                    setQuery(tabQueries[newValue].query)
                }
            },
            [tabQueries]
        )

        const getValue = useCallback(
            (
                listItem: DisplayITemType,
                dossierData: ContentItem[],
                dossierItem: Dossier
            ): ReactNode => {
                if (!dossierData || !listItem) return ''

                const singleDossierData = dossierData.find(
                    (dataItem) => dataItem.content.id === listItem.id
                )

                if (!singleDossierData?.content) {
                    const defFieldType =
                        listItem.def_dossier_def_field?.def_field?.type
                    if (
                        defFieldType !== 'list' ||
                        !listItem.settings?.fast_switch
                    ) {
                        return ''
                    }
                }

                return (
                    <>
                        <DisplayItem
                            kedo={kedo}
                            changeValue={() => {
                                //Add here
                            }}
                            content={singleDossierData?.content?.content}
                            dossier={dossierItem}
                            errors={null}
                            item={listItem}
                            key={listItem.id}
                            mode="list"
                        />
                    </>
                )
            },
            [kedo]
        )

        const handleContent = useCallback(
            (event: MouseEvent<HTMLElement>, dossierItem: Dossier) => {
                handleContentClick(event, dossierItem)
            },
            []
        )

        const listItems = useMemo(() => {
            if (!data?.items) return []
            const viewName = block.settings?.view_name ?? 'list'
            return data.items.filter((item) => item.view === viewName)
        }, [data, block.settings?.view_name])

        const layout = block.settings?.layout ?? 'list'
        const template = block.settings?.template ?? 'cardDefault'
        const showAdd = shouldShowAddButton(block, settingsLink)

        const renderCardContent = useCallback(() => {
            if (!data) return null

            const cardProps = {
                kedo,
                block,
                data,
                listItems,
                pager,
                getValue,
                handleContent,
            }

            switch (template) {
                case 'cardCarousel':
                    return <CardCarousel {...cardProps} />
                case 'cardPerson':
                    return <CardPerson {...cardProps} />
                case 'cardTimeline':
                    return <CardTimeline {...cardProps} />
                default:
                    return <CardDefault {...cardProps} />
            }
        }, [
            data,
            kedo,
            block,
            listItems,
            pager,
            getValue,
            handleContent,
            template,
        ])

        const renderCalendarContent = useCallback(() => {
            if (!data) return null

            return (
                <EventCalendarBlock
                    kedo={kedo}
                    data={data}
                    templateOptions={block.settings?.templateOptions ?? null}
                />
            )
        }, [data, kedo, block.settings?.templateOptions])

        const tableColumns = useMemo(() => {
            const columns = listItems.map((listItem) => ({
                id: String(listItem.id),
                label: translateDisplayItem(listItem, kedo),
                render: (row: {
                    dossier: Dossier
                    contentData: ContentItem[]
                }) => {
                    const isFastSwitch =
                        listItem.def_dossier_def_field?.def_field?.type ===
                            'list' && listItem.settings?.fast_switch

                    return (
                        <Box
                            onClick={
                                isFastSwitch
                                    ? (e) => e.stopPropagation()
                                    : undefined
                            }
                            sx={{ opacity: row.dossier.archived ? 0.6 : 1 }}
                        >
                            {getValue(listItem, row.contentData, row.dossier)}
                        </Box>
                    )
                },
            }))

            if (block.settings?.inline_edit) {
                columns.push({
                    id: 'actions',
                    label: '',
                    render: (row: {
                        dossier: Dossier
                        contentData: ContentItem[]
                    }) => (
                        <Box onClick={(e) => e.stopPropagation()}>
                            <DossierModalEditButton
                                dossierId={row.dossier.id}
                                kedo={kedo}
                            />
                        </Box>
                    ),
                })
            }

            return columns
        }, [listItems, kedo, block.settings?.inline_edit, getValue])

        const tableData = useMemo(() => {
            if (!data?.dossiers) return []
            return data.dossiers.map((dossierItem) => ({
                dossier: dossierItem,
                contentData: data.contents.filter(
                    (item) => item.id === dossierItem.id
                ),
            }))
        }, [data])

        const renderTableContent = useCallback(() => {
            if (loading) {
                return <LoadingState />
            }

            if (misc) {
                const isEmpty = !data?.dossiers || data.dossiers.length === 0

                return (
                    <>
                        {showAdd && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    p: 1,
                                }}
                            >
                                <DossierModalNewButton
                                    location="Block"
                                    defDossierId={block.def_dossier_id}
                                    onSuccess={handleSuccess}
                                    onClose={() => setAdd(false)}
                                    add={add}
                                    onMax={onMax}
                                    size="md"
                                    kedo={kedo}
                                />
                            </Box>
                        )}
                        {isEmpty ? (
                            <Box sx={{ p: 2 }}>
                                <Typography color="text.secondary">
                                    <FontAwesomeIcon icon={faInfo} />{' '}
                                    {kedo.t('No items found')}
                                </Typography>
                            </Box>
                        ) : (
                            <DataTable
                                columns={tableColumns}
                                data={tableData}
                                striped={false}
                                onRowClick={(row) =>
                                    handleContent(
                                        {
                                            stopPropagation: () => {
                                                //Add here
                                            },
                                        } as MouseEvent<HTMLElement>,
                                        row.dossier
                                    )
                                }
                                sx={{ boxShadow: 'none' }}
                            />
                        )}
                    </>
                )
            }

            return (
                <ContentDossierIndex
                    props={{
                        defDossierId: block.def_dossier_id,
                        kedo,
                        dossierQuery: query ?? block.dossier_query_id,
                        mode: 'block',
                        showEmail: false,
                        view: block.settings?.view_name,
                        onClick: (dossierItem: Dossier) => {
                            window.location.href = `/contentdossier/${dossierItem.id}`
                        },
                        limit: block.settings?.limit,
                        page,
                        addDossier: showAdd ? (
                            <DossierModalNewButton
                                location="Block"
                                defDossierId={block.def_dossier_id}
                                onSuccess={handleSuccess}
                                onClose={() => setAdd(false)}
                                add={add}
                                onMax={onMax}
                                size="md"
                                kedo={kedo}
                            />
                        ) : null,
                    }}
                    kedo={kedo}
                    forwardedRef={blockRef}
                />
            )
        }, [
            loading,
            misc,
            showAdd,
            data,
            tableColumns,
            tableData,
            block,
            kedo,
            query,
            page,
            add,
            onMax,
            handleSuccess,
            handleContent,
        ])

        const renderContent = useCallback(() => {
            if (loading) {
                return <LoadingState />
            }

            if (layout === 'cards') {
                return renderCardContent()
            }

            if (layout === 'calendar') {
                return renderCalendarContent()
            }

            return renderTableContent()
        }, [
            loading,
            layout,
            renderCardContent,
            renderCalendarContent,
            renderTableContent,
        ])

        const handlePageChange = useCallback(
            (newPage: number) => {
                if (misc || layout === 'cards' || layout === 'calendar') {
                    setPage(newPage)
                } else if (blockRef.current?.onChangePage) {
                    blockRef.current.onChangePage(newPage)
                } else {
                    setPage(newPage)
                }
            },
            [misc, layout, setPage]
        )

        const showExternalPagination =
            misc || layout === 'cards' || layout === 'calendar'

        const renderWithTabs = useCallback(() => {
            if (tabQueries && tabQueries.length > 0) {
                return (
                    <>
                        <Tabs value={activeTab} onChange={handleTabChange}>
                            {tabQueries.map((item, index) => (
                                <Tab key={index} label={item.title} />
                            ))}
                        </Tabs>
                        <Box sx={{ mt: 2 }}>
                            {renderContent()}
                            {showExternalPagination &&
                                pager &&
                                pager.pages > 1 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Pagination
                                            kedo={kedo}
                                            pager={pager}
                                            onChangePage={handlePageChange}
                                        />
                                    </Box>
                                )}
                        </Box>
                    </>
                )
            }

            return (
                <>
                    <Box className="block-content">{renderContent()}</Box>
                    {showExternalPagination && pager && pager.pages > 1 && (
                        <Box className="block-footer" sx={{ mt: 2 }}>
                            <Pagination
                                kedo={kedo}
                                pager={pager}
                                onChangePage={handlePageChange}
                            />
                        </Box>
                    )}
                </>
            )
        }, [
            tabQueries,
            activeTab,
            handleTabChange,
            renderContent,
            pager,
            kedo,
            showExternalPagination,
            handlePageChange,
        ])

        return (
            <BlockWrapper dashboard={dashboard}>
                <BlockHeader height={misc ? '50px' : undefined}>
                    <BlockToolbar
                        kedo={kedo}
                        block={block}
                        onClose={onClose}
                        onMax={onMax}
                        onSuccess={onSuccess}
                        fetchData={
                            layout === 'cards' || layout === 'calendar'
                                ? refetch
                                : undefined
                        }
                    />
                    {getBlockHeader?.(block, dossier)}
                </BlockHeader>
                {loading ? <LoadingState /> : data && renderWithTabs()}
            </BlockWrapper>
        )
    }
)

BlockDisplay.displayName = 'BlockDisplay'
